import { useQuery } from '@apollo/client';
import { Balance } from 'components/Balance/Balance';
import { Breadcrumbs } from 'components/Breadcrumbs/Breadcrumbs';
import { Button } from 'components/Button/Button';
import { DocumentsTab } from 'components/DocumentsTabs/DocumentsTabs';
import { ExportBalancesAsOf } from 'components/ExportBalancesAsOf/ExportBalancesAsOf';
import { Icon } from 'components/Icon/Icon';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Pagination } from 'components/Pagination/Pagination';
import { SearchFilter } from 'components/SearchFilter/SearchFilter';
import { SortFilter } from 'components/SortFilter/SortFilter';
import { Status } from 'components/Status/Status';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import dayjs from 'dayjs';
import { normalizeArray } from 'lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppRoutes } from 'Routes';
import { MANUAL_TRANSACTIONS_SEARCH, USERS_BALANCES, USERS_FULL } from 'services/users/query';

import { ConsumprionActions } from './_components/ConsumptionActions';
import { EditBalanceModal } from './_components/EditBalanceModal';
import { ViewBalancesModal } from './_components/ViewBalancesModal';

const SORT = [
    { value: 'A_Z', label: 'A-Z' },
    { value: 'Z_A', label: 'Z-A' },
    { value: 'HIGHEST_BALANCE', label: 'Highest balance' },
    { value: 'LOWEST_BALANCE', label: 'Lowest balance' },
];

const PAGE_SIZE = parseInt(localStorage.getItem('pageSize') as any) || 10;

const POSITIVE_TRANSACTIONS = ['DEPOSIT', 'CREDIT'];

export const Consumptions = () => {
    const { t } = useTranslation('documents');
    const { t: b } = useTranslation('translation');
    const [searchParams] = useSearchParams();
    const [isOpen, setIsOpen] = useState(false);
    const [balancesModal, setBalancesModal] = useState(false);
    const [member, setMember] = useState(null);
    const [allBalances, setAllBalances] = useState<any[]>([]);
    const [allUsers, setAllUsers] = useState<any[]>([]);
    const [mode, setMode] = useState<'members' | 'receipts'>('members');
    const navigate = useNavigate();

    const search = searchParams.get('search');
    const page = searchParams.get('page') || '1';
    const offset = (parseInt(page) - 1) * PAGE_SIZE;

    const { data, refetch, fetchMore } = useQuery(USERS_FULL, {
        variables: { isMember: true, first: 100 },
    });

    // Separate query to fetch ALL active members for totals calculation
    const { data: balancesData, fetchMore: fetchMoreBalances } = useQuery(USERS_BALANCES, {
        variables: {
            isMember: true,
            status: ['ACTIVE'],
            first: 100,
            offset: 0,
        },
    });

    const { data: receiptsData, refetch: refetchReceipts } = useQuery(MANUAL_TRANSACTIONS_SEARCH, {
        variables: { search, first: PAGE_SIZE, offset, sort: 'NEWEST_DATE' },
        skip: mode !== 'receipts',
    });

    // Fetch all pages of balances for totals
    useEffect(() => {
        const fetchAllBalances = async () => {
            if (!balancesData?.users) return;

            let allUsersLocal = normalizeArray(balancesData.users);
            let hasNextPage = balancesData.users.pageInfo?.hasNextPage;
            let currentOffset = 100;

            while (hasNextPage) {
                const result = await fetchMoreBalances({
                    variables: {
                        offset: currentOffset,
                    },
                });

                const newUsers = normalizeArray(result.data.users);
                allUsersLocal = [...allUsersLocal, ...newUsers];
                hasNextPage = result.data.users.pageInfo?.hasNextPage;
                currentOffset += 100;
            }

            setAllBalances(allUsersLocal);
        };

        fetchAllBalances();
    }, [balancesData, fetchMoreBalances]);

    // Fetch all pages of users for the table
    useEffect(() => {
        const fetchAllUsers = async () => {
            if (!data?.users) return;

            const searchVal = searchParams.get('search');
            const sort = searchParams.get('sort');

            let allUsersData = normalizeArray(data.users);
            let hasNextPage = data.users.pageInfo?.hasNextPage;
            let currentOffset = 100;

            while (hasNextPage) {
                const result = await fetchMore({
                    variables: {
                        search: searchVal,
                        sort,
                        offset: currentOffset,
                    },
                });

                const newUsers = normalizeArray(result.data.users);
                allUsersData = [...allUsersData, ...newUsers];
                hasNextPage = result.data.users.pageInfo?.hasNextPage;
                currentOffset += 100;
            }

            setAllUsers(allUsersData);
        };

        fetchAllUsers();
    }, [data, fetchMore, searchParams]);

    // Refetch when search or sort changes
    useEffect(() => {
        const searchVal = searchParams.get('search');
        const sort = searchParams.get('sort');

        refetch({
            search: searchVal,
            sort: sort,
            offset: 0,
            first: 100,
        });
    }, [searchParams, refetch]);

    // Refetch receipts when search params change (mode === 'receipts')
    useEffect(() => {
        if (mode !== 'receipts') return;
        refetchReceipts({
            search,
            first: PAGE_SIZE,
            offset,
            sort: 'NEWEST_DATE',
        });
    }, [searchParams, mode, refetchReceipts, search, offset]);

    const totals = useMemo(() => {
        if (!allBalances.length) {
            return { totalBalance: 0, totalRemainderBalance: 0, currency: 'CHF' };
        }

        const totalBalance = allBalances.reduce((sum: number, user: any) => {
            return sum + parseFloat(user.balance?.amount || 0);
        }, 0);

        const totalRemainderBalance = allBalances.reduce((sum: number, user: any) => {
            return sum + parseFloat(user.remainderBalance || 0);
        }, 0);

        const currency = allBalances[0]?.balance?.currency || 'CHF';

        return { totalBalance, totalRemainderBalance, currency };
    }, [allBalances]);

    const editMembersBalance = (user: any) => {
        setMember(user);
        setIsOpen(true);
    };

    const viewMemberBalances = (user: any) => {
        setMember(user);
        setBalancesModal(true);
    };

    const openConsumptions = (id: string) => {
        navigate(AppRoutes.memberDocumentConsumptions.replace(':id', id));
    };

    const receipts = normalizeArray(receiptsData?.balanceEntries);
    const receiptsTotal = receiptsData?.balanceEntries?.totalCount ?? 0;
    const receiptsPageInfo = receiptsData?.balanceEntries?.pageInfo;

    return (
        <div>
            <Breadcrumbs crumbs={[b('breadcrumbs.dashboard'), b('breadcrumbs.consumptions')]} />
            <DocumentsTab />
            <PageTitle>{t('consumptionsTitle')}</PageTitle>

            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
                <Button variant={mode === 'members' ? 'default' : 'outline'} onClick={() => setMode('members')}>
                    {t('membersTab')}
                </Button>
                <Button variant={mode === 'receipts' ? 'default' : 'outline'} onClick={() => setMode('receipts')}>
                    {t('receiptsTab')}
                </Button>
            </div>

            {mode === 'members' && (
                <>
                    <div className="flex gap-4 flex-wrap pb-4 justify-between items-center">
                        <div className="flex gap-4 flex-wrap">
                            <SearchFilter placeholder={t('searchPlaceholder')} />
                            <SortFilter options={SORT} />
                        </div>
                        <div className="flex gap-4 items-center flex-wrap">
                            <ExportBalancesAsOf />
                            <div className="flex gap-6 px-4 py-2 bg-zinc-50 border rounded">
                                <div className="flex flex-col items-end">
                                    <span className="text-sm text-zinc-600 font-medium">{t('balanceLabel')}</span>
                                    <Balance balance={{ amount: totals.totalBalance, currency: totals.currency }} />
                                </div>
                                <div className="flex flex-col items-end">
                                    <span className="text-sm text-zinc-600 font-medium">
                                        {t('remainderBalanceLabel')}
                                    </span>
                                    <Balance
                                        balance={{ amount: totals.totalRemainderBalance, currency: totals.currency }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <Table className="border">
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('nameLabel')}</TableHead>
                                <TableHead>{t('phoneLabel')}</TableHead>
                                <TableHead>{t('balanceLabel')}</TableHead>
                                <TableHead>{t('remainderBalanceLabel')}</TableHead>
                                <TableHead>{t('totalBalanceLabel')}</TableHead>
                                <TableHead>{t('statusLabel')}</TableHead>
                                <TableHead />
                            </TableRow>
                        </TableHeader>
                        <TableBody className="hover:cursor-pointer">
                            {allUsers.length > 0 &&
                                allUsers.map((user: any) => (
                                    <TableRow key={user.id} className="text-zinc-700 hover:bg-rose-50">
                                        <TableCell
                                            className="flex gap-2 items-center"
                                            onClick={() => openConsumptions(user.id)}
                                        >
                                            <UserImage image={user.image} />
                                            {user.lastName || '-'}, {user.firstName || '-'}
                                        </TableCell>
                                        <TableCell onClick={() => openConsumptions(user.id)}>
                                            {user.phoneNumber}
                                        </TableCell>
                                        <TableCell
                                            className="hidden md:table-cell"
                                            onClick={() => openConsumptions(user.id)}
                                        >
                                            <Balance balance={user.balance} />
                                        </TableCell>
                                        <TableCell
                                            className="hidden md:table-cell"
                                            onClick={() => openConsumptions(user.id)}
                                        >
                                            <Balance
                                                balance={{
                                                    currency: user.balance?.currency || 'CHF',
                                                    amount: user.remainderBalance || 0,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            className="hidden md:table-cell"
                                            onClick={() => openConsumptions(user.id)}
                                        >
                                            <Balance
                                                balance={{
                                                    currency: user.balance?.currency || 'CHF',
                                                    amount:
                                                        parseFloat(user.balance?.amount || 0) +
                                                        parseFloat(user.remainderBalance || 0),
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            className="hidden md:table-cell"
                                            onClick={() => openConsumptions(user.id)}
                                        >
                                            <Status status={user.status} />
                                        </TableCell>
                                        <TableCell className="py-3 justify-end flex">
                                            <ConsumprionActions
                                                user={user}
                                                onEdit={() => editMembersBalance(user)}
                                                onViewBalances={() => viewMemberBalances(user)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </>
            )}

            {mode === 'receipts' && (
                <>
                    <div className="flex gap-4 flex-wrap pb-4">
                        <SearchFilter placeholder={t('searchReceiptPlaceholder')} />
                    </div>

                    {!search && <div className="py-10 text-center text-zinc-400">{t('enterReceiptNumberPrompt')}</div>}

                    {search && receiptsTotal === 0 && (
                        <div className="py-5 text-center text-zinc-700">{t('noReceiptsFound')}</div>
                    )}

                    {search && receiptsTotal > 0 && (
                        <>
                            <Table className="border">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('dateLabel')}</TableHead>
                                        <TableHead className="whitespace-nowrap">{t('receiptLabel')}</TableHead>
                                        <TableHead>{t('memberColumnLabel')}</TableHead>
                                        <TableHead>{t('typeLabel')}</TableHead>
                                        <TableHead>{t('amountLabel')}</TableHead>
                                        <TableHead>{t('noteLabel')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody className="hover:cursor-pointer">
                                    {receipts.map((node: any) => (
                                        <TableRow
                                            key={node.id}
                                            className="text-zinc-700 hover:bg-rose-50"
                                            onClick={() => {
                                                if (node.member?.id) {
                                                    openConsumptions(node.member.id);
                                                }
                                            }}
                                        >
                                            <TableCell>{dayjs(node.date).format('DD.MM.YYYY')}</TableCell>
                                            <TableCell>{node.invoiceNumber}</TableCell>
                                            <TableCell>
                                                <ReceiptMemberCell node={node} />
                                            </TableCell>
                                            <TableCell>{t(node.type)}</TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2 min-w-[max-content]">
                                                    <Balance
                                                        classname={
                                                            POSITIVE_TRANSACTIONS.includes(node.type)
                                                                ? 'text-green-700'
                                                                : 'text-red-700'
                                                        }
                                                        balance={{ amount: node.amount, currency: 'CHF' }}
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell>{node.note}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Pagination pageInfo={receiptsPageInfo} total={receiptsTotal} />
                        </>
                    )}
                </>
            )}

            <EditBalanceModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                member={member}
                onUpdate={() => refetch()}
            />
            <ViewBalancesModal isOpen={balancesModal} onClose={() => setBalancesModal(false)} member={member} />
        </div>
    );
};

const ReceiptMemberCell = ({ node }: { node: any }) => {
    if (node.member) {
        return (
            <span>
                {node.member.lastName}, {node.member.firstName}{' '}
                <span className="text-zinc-400 text-sm">{node.member.memberId}</span>
            </span>
        );
    }
    if (node.company) {
        return <span>{node.company.name}</span>;
    }
    return <span>-</span>;
};

const UserImage = ({ image }: any) => {
    return (
        <div className="bg-zinc-300 w-[32px] h-[32px] flex justify-center items-center">
            {image ? (
                <img src={`${image.url}?fit=facearea&facepad=2.5&w=32&h=32`} alt={image.alt} />
            ) : (
                <Icon name="user" className="text-zinc-50 h-[18px]" />
            )}
        </div>
    );
};
